import { Component, effect, input, signal } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs/internal/operators/finalize';
import { ApplicableFor, PaymentFlowType, PaymentStatus } from 'src/app/interfaces/payment-enums';
import { PaymentInformationModel } from 'src/app/interfaces/payment-information';
import { CollectPaymentComponent } from 'src/app/shared/collect-payment/collect-payment.component';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { ResultOfT } from 'src/app/shared/models/response/result';
import { PaymentHistoryComponent } from 'src/app/shared/payment-history/payment-history.component';
import { PaymentService } from 'src/app/shared/services/payment.service';
import { SidebarService } from '../../servicies/sidebar.service';
import { CommonFunctions } from 'src/app/shared/common-functions';
import { featureResponseModel } from 'src/app/shared/models/response/feature-group-response';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';

@Component({
  selector: 'app-payment-information',
  templateUrl: './payment-information.component.html',
  styleUrl: './payment-information.component.sass',
  providers: [DialogService],
  standalone: false
})
export class PaymentInformationComponent extends BaseComponent {
  salesOrderId = input.required<number>();
  detailedView = input.required<boolean>();
  paymentFlowType = PaymentFlowType;
  paymentMenu: MenuItem[] = [];
  paymentData = signal<PaymentInformationModel>(null);
  features: featureResponseModel[];
  isPaymentPermission: boolean = false;

  constructor(private webStorageService: WebStorageService,private paymentService: PaymentService, private dialogService: DialogService, messageService: MessageService, private sidebarService: SidebarService) {
    super(messageService);

    effect(() => {
      const salesOrderId = this.salesOrderId();
      if (salesOrderId && salesOrderId > 0) {
        this.getPaymentInformation();
      }
    });

    this.features = this.webStorageService.getPermissions();
    if (this.features != null && this.features != undefined) {
      if (this.features && this.features.length > 0) {
        let paymentFeature = this.features.find((x) => x.appModuleFeatureName.toLowerCase() == 'payment');
        if (paymentFeature && paymentFeature.isActive == true) this.isPaymentPermission = true;
        else this.isPaymentPermission = false;
      }
    }
  }

  getPaymentFlowType(): PaymentFlowType {
    return this.paymentData()?.paymentCard == null ? PaymentFlowType.SaveOnly : PaymentFlowType.PayOnly;
  }

  get shouldShowLoading(): boolean {
    return  this.isPaymentPermission && this.paymentData()?.enabled === true && this.loading;
  }

  get shouldShowPaymentInfo(): boolean {
    return this.isPaymentPermission && this.paymentData()?.enabled === true && !this.loading;
  }
  
  getPaymentInformation() {
    this.loading = true;
    this.paymentMenu = [];
    this.paymentService
      .getPaymentInformation(this.salesOrderId())
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((result: ResultOfT<PaymentInformationModel>) => {
        const data = result.responseModel;
        this.paymentData.set({ ...data, originalAmount: data.paymentAmount });
        if (data.paymentAmount > 0 && !data.paymentCard) {
          this.paymentMenu = [
            {
              label: 'Add Card',
              command: () => {
                this.openPaymentPopup(PaymentFlowType.SaveOnly);
              }
            },
            {
              label: 'Add Card & Collect Payment',
              command: () => {
                this.openPaymentPopup(PaymentFlowType.PayAndSave);
              }
            }
          ];
        }
      });
  }

  deletePaymentCard(id: number) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        data: {
          message: 'Are you sure you want to delete this payment card?'
        },
        header: 'Confirmation'
      })
      .onClose.subscribe((response: boolean) => {
        if (!response) return;
        this.loading = true;
        this.paymentService
          .deleteCard(id)
          .pipe(finalize(() => (this.loading = false)))
          .subscribe(() => {
            this.showSuccessMessage('Card deleted successfully.');
            this.getPaymentInformation();
          });
      });
  }

  openPaymentPopup(FlowType: PaymentFlowType) {
    if (this.isPaymentDisabled()) {
      return;
    }
    
    let dialogRef: DynamicDialogRef;
    dialogRef = this.dialogService.open(CollectPaymentComponent, {
      header: 'Add New Credit Card',
      width: '700px',
      height: '95%',
      modal: true,
      closable: true,
      data: {
        salesOrderId: this.salesOrderId(),
        flowType: FlowType,
        applicableFor: ApplicableFor.DME
      },
      styleClass: 'add-card-dialog'
    });

    dialogRef.onClose.subscribe((result) => {
      this.getPaymentInformation();
    });
  }

  private isPaymentDisabled(): boolean {
    return this.paymentData()?.isExcluded || this.paymentData()?.salesOrderStatus === 'void';
  }

  showPaymentHistory() {
    this.sidebarService.open(PaymentHistoryComponent, {
      salesOrderId: this.salesOrderId(),
      header: 'Payment History',
      width: '650px',
      styleClass: 'payment-sidebar-popup',
      style: 'payment-sidebar-popup',
    });
  }

  saveAmount(): void {
    if (CommonFunctions.isNumberNullOrUndefined(this.paymentData()?.originalAmount)) {
      this.showErrorMessage('Please enter a valid amount.');
      this.paymentData.update((data) => (data ? { ...data, isAmountEditing: true } : data));
      return;
    }
    this.loading = true;
    this.paymentService
      .updatePaymentAmount({
        paymentAmount: this.paymentData().originalAmount,
        salesOrderId: this.salesOrderId(),
        paymentStatus: PaymentStatus.Pending
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(() => {
        this.getPaymentInformation();
      });
  }

  allowPositiveOnly(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.key === '.') {
      const input = event.target as HTMLInputElement;
      if (input.value.includes('.')) {
        event.preventDefault();
      }
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  sanitizeAmount() {
    const amount = this.paymentData().originalAmount;
    if (amount < 0 || isNaN(amount)) {
      this.paymentData().originalAmount = null;
    }
  }

}
